import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import BaseModal from 'src/components/BaseModal';
import { BaseModalProps } from 'src/components/BaseModal/BaseModal';
import { Loader } from 'src/components';
import {
  useAiRatesQuery,
  usePlansQuery,
} from 'src/api/queries/subscriptionQueries';
import { TAiRates } from 'src/api/functions/subscriptionApi';

import messages from './messages';
import styles from './styles.module.scss';

/**
 * "What do credits buy?" — the per-operation price list behind the rate
 * card and the top-up packs. The unit is "credits", not "AI credits", since
 * it also pays for print export beyond the plan allowance (2026-10-13).
 *
 * Numbers come from GET /api/v1/billing/ai-rates (`ai_credit_rates`, which
 * sysadmin edits without a deploy) — NEVER hardcoded here. What IS ours is
 * the presentation whitelist below: which rate keys this release shows, under
 * which customer-facing names — so a new backend row never renders a raw key
 * to customers by surprise.
 *
 * Video (conf/22) is priced per minute of uploaded video, so its rows read
 * "N credits / min" rather than per operation.
 *
 * The enhancement group mirrors how the photo-edit panel presents itself
 * (Auto Enhance / AI Restore / Remove object / Custom enhancement — the
 * pipeline is one charge, the `sequence` rate) rather than the underlying
 * per-verb rows, so the modal and the editor tell one story.
 */

// The release whitelist: flat-rate rows shown as their own line.
const TOP_LEVEL_ROWS: { key: string; label: keyof typeof messages }[] = [
  { key: 'bgremove', label: 'aiCostsBgRemove' },
  { key: 'image_analysis', label: 'aiCostsAutoTag' },
  { key: 'face_recognition', label: 'aiCostsFaceRec' },
];

// Video (conf/22): credits per minute of uploaded video, metered per second.
const VIDEO_ROWS: { key: string; label: keyof typeof messages }[] = [
  { key: 'video_sd', label: 'aiCostsVideoSd' },
  { key: 'video_hd', label: 'aiCostsVideoHd' },
  { key: 'video_4k', label: 'aiCostsVideo4k' },
  { key: 'video_hls', label: 'aiCostsVideoHls' },
  { key: 'video_storyboard', label: 'aiCostsVideoStoryboard' },
];

// The enhancement group's entries, in the edit panel's own order.
const ENHANCE_ROWS: { key: string; label: keyof typeof messages }[] = [
  { key: 'auto_enhance', label: 'aiCostsAutoEnhance' },
  { key: 'remove_object', label: 'aiCostsRemoveObject' },
  { key: 'ai_restore', label: 'aiCostsAiRestore' },
  { key: 'sequence', label: 'aiCostsCustomEnhance' },
];

// The "typical agent message" behind the headline estimate: ~1K tokens in,
// ~1K out, a couple of tool calls. An assumption, not a promise — but scaling
// it from the live rates means a rate edit moves the estimate too.
const TYPICAL_MESSAGE = { inputTokens: 1000, outputTokens: 1000, toolCalls: 2 };

const usd = (credits: number, anchor: number | null) => {
  if (!anchor) return null;
  const value = credits * anchor;
  // Two decimals when they suffice ($0.05, $0.20), three when they don't
  // ($0.075) — never a trailing zero pretending at precision ($0.050).
  return `$${value.toFixed(Number.isInteger(value * 100) ? 2 : 3)}`;
};

const Row: React.FC<{
  label: React.ReactNode;
  credits: number;
  anchor: number | null;
  sub?: boolean;
  perMinute?: boolean;
}> = ({ label, credits, anchor, sub, perMinute }) => {
  const dollars = usd(credits, anchor);
  return (
    <li className={sub ? styles.aiCostsSubRow : styles.rateCardRow}>
      <span>{label}</span>
      <span className={styles.rateCardPrice}>
        <FormattedMessage
          {...(perMinute ? messages.aiCostsCreditsPerMinute : messages.aiCostsCredits)}
          values={{ count: credits }}
        />
        {dollars && <span className={styles.aiCostsUsd}> · {dollars}</span>}
      </span>
    </li>
  );
};

const AiCreditCostsModal = (rest: BaseModalProps) => {
  const intl = useIntl();
  const { data: rates, isLoading } = useAiRatesQuery();
  const { data: catalog } = usePlansQuery({ retry: false });

  // The $-per-credit anchor, read from the same catalog the rate card uses
  // (any plan that bills ai_credits carries it). Null ⇒ credits only.
  const anchor = React.useMemo(() => {
    for (const plan of catalog?.plans ?? []) {
      const rate = plan.quotas?.ai_credits?.overage_rate_usd;
      if (rate) return rate;
    }
    return null;
  }, [catalog]);

  const flat: TAiRates['flat'] = rates?.flat ?? {};
  const agent = rates?.token?.default;
  const agentTypical = agent
    ? Math.max(1, Math.round(
        agent.input_rate * TYPICAL_MESSAGE.inputTokens
        + agent.output_rate * TYPICAL_MESSAGE.outputTokens
        + agent.tool_call_rate * TYPICAL_MESSAGE.toolCalls,
      ))
    : null;

  const enhanceRows = ENHANCE_ROWS.filter((r) => flat[r.key] !== undefined);
  const videoRows = VIDEO_ROWS.filter((r) => flat[r.key] !== undefined);

  // Print export past the plan allowance — priced by the backend (one
  // background removal) and published on every plan that has print export.
  const printOverage = React.useMemo(() => {
    for (const plan of catalog?.plans ?? []) {
      const credits = plan.quotas?.print_exports?.overage_ai_credits;
      if (credits) return credits;
    }
    return null;
  }, [catalog]);

  return (
    <BaseModal {...rest}>
      <h3>
        <FormattedMessage {...messages.aiCostsModalTitle} />
      </h3>
      {isLoading || !rates ? (
        <div className={styles.loadingContainer}>
          <Loader />
        </div>
      ) : (
        <div className={styles.aiCostsBody}>
          {anchor && (
            <p className={styles.billingSubtitle}>
              <FormattedMessage
                {...messages.aiCostsAnchorLine}
                values={{ price: anchor }}
              />
            </p>
          )}

          <ul className={styles.aiCostsList}>
            {TOP_LEVEL_ROWS.filter((r) => flat[r.key] !== undefined).map((r) => (
              <Row
                key={r.key}
                label={intl.formatMessage(messages[r.label])}
                credits={flat[r.key]}
                anchor={anchor}
              />
            ))}
          </ul>

          {enhanceRows.length > 0 && (
            <>
              <h4 className={styles.aiCostsGroupHeading}>
                <FormattedMessage {...messages.aiCostsEnhanceGroup} />
              </h4>
              <ul className={styles.aiCostsList}>
                {enhanceRows.map((r) => (
                  <Row
                    key={r.key}
                    label={intl.formatMessage(messages[r.label])}
                    credits={flat[r.key]}
                    anchor={anchor}
                    sub
                  />
                ))}
              </ul>
              <p className={styles.aiCostsNote}>
                <FormattedMessage {...messages.aiCostsPreviewNote} />
              </p>
            </>
          )}

          {videoRows.length > 0 && (
            <>
              <h4 className={styles.aiCostsGroupHeading}>
                <FormattedMessage {...messages.aiCostsVideoGroup} />
              </h4>
              <ul className={styles.aiCostsList}>
                {videoRows.map((r) => (
                  <Row
                    key={r.key}
                    label={intl.formatMessage(messages[r.label])}
                    credits={flat[r.key]}
                    anchor={anchor}
                    sub
                    perMinute
                  />
                ))}
              </ul>
              {flat.video_hls !== undefined && (
                <p className={styles.aiCostsNote}>
                  <FormattedMessage
                    {...messages.aiCostsVideoNote}
                    values={{ hundredMinutes: flat.video_hls * 100 }}
                  />
                </p>
              )}
            </>
          )}

          {printOverage && (
            <>
              <h4 className={styles.aiCostsGroupHeading}>
                <FormattedMessage {...messages.aiCostsProcessingGroup} />
              </h4>
              <ul className={styles.aiCostsList}>
                <Row
                  label={intl.formatMessage(messages.aiCostsPrintExport)}
                  credits={printOverage}
                  anchor={anchor}
                  sub
                />
              </ul>
            </>
          )}

          {agent && agentTypical && (
            <>
              <h4 className={styles.aiCostsGroupHeading}>
                <FormattedMessage {...messages.aiCostsAgent} />
              </h4>
              <p className={styles.aiCostsAgentHeadline}>
                <FormattedMessage
                  {...messages.aiCostsAgentApprox}
                  values={{ count: agentTypical }}
                />
              </p>
              <p className={styles.aiCostsNote}>
                <FormattedMessage
                  {...messages.aiCostsAgentDetail}
                  values={{
                    input: agent.input_rate * 1000,
                    output: agent.output_rate * 1000,
                    tool: agent.tool_call_rate,
                  }}
                />
              </p>
            </>
          )}
        </div>
      )}
    </BaseModal>
  );
};

export default AiCreditCostsModal;
